import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { COMPANY_INFO, NAV_LINKS } from '../data/mockData';

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  // Handle click outside dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (href) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm border-b border-slate-200">
      {/* Top Utility Bar */}
      <div className="bg-slate-900 text-slate-300 text-xs py-2 px-4 sm:px-6 lg:px-8 border-b border-slate-800">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-between items-center gap-y-1">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="inline-flex items-center gap-1.5 font-medium text-sky-400">
              <i className="fa-solid fa-award" aria-hidden="true"></i>
              <span>{COMPANY_INFO.experienceYears} års erfaring</span>
            </span>
            <span className="hidden sm:inline-block text-slate-700">•</span>
            <span className="hidden sm:inline-flex items-center gap-1.5 text-slate-300">
              <i className="fa-solid fa-map-location-dot text-slate-400" aria-hidden="true"></i>
              <span>{COMPANY_INFO.coverageRegions}</span>
            </span>
          </div>

          <div className="flex items-center gap-5 ml-auto text-xs font-semibold">
            <a 
              href={`tel:${COMPANY_INFO.phoneRaw}`} 
              className="inline-flex items-center gap-1.5 text-white hover:text-sky-400 transition-colors duration-200 nowrap-contact"
            >
              <i className="fa-solid fa-phone text-sky-400" aria-hidden="true"></i>
              <span>{COMPANY_INFO.phone}</span>
            </a>
            <a 
              href={`mailto:${COMPANY_INFO.email}`} 
              className="hidden md:inline-flex items-center gap-1.5 text-slate-300 hover:text-sky-400 transition-colors duration-200 nowrap-contact"
            >
              <i className="fa-solid fa-envelope text-slate-400" aria-hidden="true"></i>
              <span>{COMPANY_INFO.email}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          
          {/* Logo / Brand Name */}
          <Link to="/" className="flex items-center gap-3 group focus:outline-none focus:ring-2 focus:ring-sky-500 rounded-lg p-1">
            <img 
              src={COMPANY_INFO.logo} 
              alt="K-TO Ventilationsteknik Logo" 
              className="w-auto h-10 md:h-12 lg:h-14 max-h-[56px] object-contain"
            />
          </Link>

          {/* Desktop Navigation Link Items (Visible ONLY on >= 1200px screens) */}
          <nav className="hidden min-[1200px]:flex items-center gap-1">
            {NAV_LINKS.map((item) => {
              if (item.children) {
                const isChildActive = item.children.some(child => location.pathname === child.href);
                return (
                  <div 
                    key={item.label} 
                    className="relative"
                    ref={dropdownRef}
                    onMouseEnter={() => setDropdownOpen(true)}
                    onMouseLeave={() => setDropdownOpen(false)}
                  >
                    <button
                      type="button"
                      onClick={() => setDropdownOpen(!dropdownOpen)}
                      className={`px-3.5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center gap-1.5 focus:outline-none focus:ring-2 focus:ring-sky-500 ${
                        isChildActive || dropdownOpen
                          ? 'text-sky-600 bg-sky-50 font-bold'
                          : 'text-slate-700 hover:text-slate-900 hover:bg-slate-100'
                      }`}
                      aria-expanded={dropdownOpen}
                    >
                      <span>{item.label}</span>
                      <i className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${dropdownOpen ? 'rotate-180 text-sky-600' : 'text-slate-400'}`} aria-hidden="true"></i>
                    </button>

                    {/* Dropdown Menu */}
                    {dropdownOpen && (
                      <div className="absolute top-full left-0 w-64 pt-2 shadow-xl z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                        <div className="bg-white rounded-xl border border-slate-200/90 shadow-2xl p-2 space-y-0.5">
                          {item.children.map((child) => (
                            <Link
                              key={child.href}
                              to={child.href}
                              className={`block px-3 py-2.5 rounded-lg text-xs font-semibold transition-colors duration-150 ${
                                location.pathname === child.href
                                  ? 'bg-sky-50 text-sky-700 font-bold'
                                  : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
                              }`}
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              const active = isActive(item.href);
              return (
                <Link
                  key={item.label}
                  to={item.href}
                  className={`px-3.5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                    active
                      ? 'text-sky-600 bg-sky-50 font-bold border-b-2 border-sky-600'
                      : 'text-slate-700 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Header Action Button & Mobile Burger Toggle (Visible ONLY on screens < 1200px) */}
          <div className="flex items-center gap-3">
            <a
              href={`tel:${COMPANY_INFO.phoneRaw}`}
              className="hidden sm:inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white px-4 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-all duration-200 shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 nowrap-contact"
            >
              <i className="fa-solid fa-phone" aria-hidden="true"></i>
              <span>Ring 22 72 87 23</span>
            </a>

            {/* Mobile / Tablet Burger Button (Shown ONLY under 1200px per strict spec) */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="min-[1200px]:hidden p-2.5 rounded-xl text-slate-700 hover:text-slate-900 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-sky-500"
              aria-label="Åbn eller luk hovedmenu"
            >
              <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark text-xl' : 'fa-bars text-xl'}`} aria-hidden="true"></i>
            </button>
          </div>

        </div>
      </div>

      {/* Mobile & Tablet Drawer Menu (< 1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden bg-white border-b border-slate-200 px-4 pt-3 pb-6 space-y-2 animate-in slide-in-from-top duration-200 max-h-[85vh] overflow-y-auto">
          {NAV_LINKS.map((item) => {
            if (item.children) {
              return (
                <div key={item.label} className="space-y-1">
                  <div className="px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center justify-between">
                    <span>{item.label}</span>
                  </div>
                  <div className="pl-3 border-l-2 border-sky-200 space-y-1">
                    {item.children.map((child) => (
                      <Link
                        key={child.href}
                        to={child.href}
                        className={`block px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                          location.pathname === child.href
                            ? 'bg-sky-50 text-sky-600 font-bold'
                            : 'text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                </div>
              );
            }

            return (
              <Link
                key={item.label}
                to={item.href}
                className={`block px-3 py-2.5 rounded-lg text-base font-semibold transition-colors ${
                  isActive(item.href)
                    ? 'bg-sky-50 text-sky-600 font-bold'
                    : 'text-slate-800 hover:bg-slate-50'
                }`}
              >
                {item.label}
              </Link>
            );
          })}

          <div className="pt-4 border-t border-slate-100 space-y-3">
            <a
              href={`tel:${COMPANY_INFO.phoneRaw}`}
              className="flex items-center justify-center gap-2 bg-sky-600 text-white py-3 rounded-xl font-bold text-sm shadow"
            >
              <i className="fa-solid fa-phone" aria-hidden="true"></i>
              <span>Ring på 22 72 87 23</span>
            </a>
            <a
              href={`mailto:${COMPANY_INFO.email}`}
              className="flex items-center justify-center gap-2 bg-slate-100 text-slate-800 py-3 rounded-xl font-bold text-sm"
            >
              <i className="fa-solid fa-envelope" aria-hidden="true"></i>
              <span>{COMPANY_INFO.email}</span>
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
